import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router";
import Alert from "@mui/material/Alert";
import { getOrderCustomerList } from "@/api/customer/getOrderCustomerList";
import type { GetOrderListArgs } from "@/api/order/getOrderList";
import { getOrderPage } from "@/api/order/getOrderPage";
import { useDebouncedValue } from "@/app/useDebouncedValue";
import { ListGrid } from "@/components/list-grid/ListGrid";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { ListGridFooter } from "@/components/list-grid/ListGridFooter";
import { DETAIL_KIND, toDetailLink } from "@/features/detail/detailKind";
import { ORDER_LIST_PATH } from "@/features/order/order.paths";
import { isOrderListFilterActive, OrderListFilterPanel } from "./order-list-filter/OrderListFilterPanel";
import type { OrderListFilterValues } from "./order-list-filter/OrderListFilterPanel";
import {
  isOrderListFilterPanelOpenInSearchParams,
  toOrderListFilterFromSearchParams,
  writeOrderListFilterToSearchParams,
} from "./order-list-filter/orderListFilterSearchParams";
import { toOrderListFilterCustomerList } from "./order-list-filter/toOrderListFilterCustomerList";
import { OrderListHeader } from "./OrderListHeader";
import { OrderListRow } from "./OrderListRow";
import { OrderListSkeletonRow } from "./OrderListSkeletonRow";

const OPEN_PARAM = "open";
const PAGE_PARAM = "page";
const PAGE_SIZE_PARAM = "pageSize";
const PAGE_SIZES = [50, 100, 200];
const DEFAULT_PAGE_SIZE = 100;

const FILTER_DEBOUNCE_MS = 300;

const toGetOrderListArgs = (filter: OrderListFilterValues): GetOrderListArgs => {
  if (!isOrderListFilterActive(filter)) {
    return {};
  }
  return {
    search: filter.search,
    sources: filter.sources,
    statuses: filter.statuses,
    customerIds: filter.customerIds,
    orderDateFrom: filter.orderDateFrom ?? undefined,
    orderDateTo: filter.orderDateTo ?? undefined,
  };
};

/** The URL's 1-based page as a zero-based index; anything invalid is the first page. */
const toPageFromSearchParams = (params: URLSearchParams): number => {
  const page = Number(params.get(PAGE_PARAM));
  if (!Number.isInteger(page) || page < 1) return 0;
  return page - 1;
};

const toPageSizeFromSearchParams = (params: URLSearchParams): number => {
  const pageSize = Number(params.get(PAGE_SIZE_PARAM));
  if (PAGE_SIZES.includes(pageSize)) return pageSize;
  return DEFAULT_PAGE_SIZE;
};

/** Rewrites the address bar's query string in place (replaceState), without a router navigation. */
const replaceSearchParamsInUrl = (update: (params: URLSearchParams) => void) => {
  const url = new URL(window.location.href);
  update(url.searchParams);
  window.history.replaceState(window.history.state, "", url);
};

/**
 * The orders page: a paged grid (GET /orders ?offset=&limit=) with expandable rows, a filter
 * side panel and the detail stack over it. Page, page size, expanded rows (?open=) and filters
 * are mirrored in the URL.
 */
export const OrderList = () => {
  const [searchParams] = useSearchParams();
  const [openOrderIds, setOpenOrderIds] = useState(() => searchParams.getAll(OPEN_PARAM));
  const [filter, setFilter] = useState<OrderListFilterValues>(() => toOrderListFilterFromSearchParams(searchParams));
  const [isFilterOpen, setIsFilterOpen] = useState(() => isOrderListFilterPanelOpenInSearchParams(searchParams));
  const [page, setPage] = useState(() => toPageFromSearchParams(searchParams));
  const [pageSize, setPageSize] = useState(() => toPageSizeFromSearchParams(searchParams));
  const appliedFilter = useDebouncedValue(filter, FILTER_DEBOUNCE_MS);
  const listArgs = useMemo(() => toGetOrderListArgs(appliedFilter), [appliedFilter]);
  const pageArgs = useMemo(() => ({ ...listArgs, offset: page * pageSize, limit: pageSize }), [listArgs, page, pageSize]);
  const query = getOrderPage.useQuery(pageArgs);
  const customersQuery = getOrderCustomerList.useQuery();
  const navigate = useNavigate();
  const filterCustomers = useMemo(() => toOrderListFilterCustomerList(customersQuery.data ?? []), [customersQuery.data]);
  const orders = query.data?.orders ?? [];
  const total = query.data?.total ?? 0;
  let footerSummary = `${total.toLocaleString()} orders`;
  if (isOrderListFilterActive(appliedFilter)) footerSummary = `${total.toLocaleString()} orders match the filters`;
  let emptyText: string | null = null;
  if (orders.length === 0 && !query.isFetching) {
    emptyText = "No orders yet.";
    if (isOrderListFilterActive(appliedFilter)) emptyText = "No orders match these filters.";
  }

  const handleFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleFilterClose = () => {
    setIsFilterOpen(false);
  };

  const handleFilterChange = (nextFilter: OrderListFilterValues) => {
    setFilter(nextFilter);
    setPage(0);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  const handleSelect = useCallback((orderId: string) => {
    navigate(toDetailLink(ORDER_LIST_PATH, DETAIL_KIND.orders, orderId));
  }, [navigate]);

  const handleToggle = useCallback((orderId: string) => {
    setOpenOrderIds((previous) => {
      if (previous.includes(orderId)) {
        return previous.filter((id) => id !== orderId);
      }
      return [...previous, orderId];
    });
  }, []);

  useEffect(() => {
    replaceSearchParamsInUrl((params) => {
      params.delete(OPEN_PARAM);
      for (const orderId of openOrderIds) params.append(OPEN_PARAM, orderId);
    });
  }, [openOrderIds]);

  useEffect(() => {
    replaceSearchParamsInUrl((params) => writeOrderListFilterToSearchParams(params, filter, isFilterOpen));
  }, [filter, isFilterOpen]);

  useEffect(() => {
    replaceSearchParamsInUrl((params) => {
      params.delete(PAGE_PARAM);
      params.delete(PAGE_SIZE_PARAM);
      if (page > 0) params.set(PAGE_PARAM, String(page + 1));
      if (pageSize !== DEFAULT_PAGE_SIZE) params.set(PAGE_SIZE_PARAM, String(pageSize));
    });
  }, [page, pageSize]);

  useEffect(() => {
    if (!query.data || query.isFetching) return;
    const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [page, pageSize, query.data, query.isFetching, total]);

  if (query.isError) {
    return (
      <Alert severity="error">
        Failed to load orders: {JSON.stringify(query.error)}
      </Alert>
    );
  }

  return (
    <>
      <OrderListFilterPanel
        customers={filterCustomers}
        filter={filter}
        isOpen={isFilterOpen}
        onChange={handleFilterChange}
        onClose={handleFilterClose}
      />
      <Page isLocked>
        <PageHeader title="Orders" />
        <ListGrid
          emptyText={emptyText}
          footer={(
            <ListGridFooter
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              page={page}
              pageSize={pageSize}
              pageSizes={PAGE_SIZES}
              summary={footerSummary}
              total={total}
            />
          )}
          header={(
            <OrderListHeader
              isFilterActive={isOrderListFilterActive(filter)}
              onFilterToggle={handleFilterToggle}
            />
          )}
          isFetching={query.isFetching}
          isLoading={query.isLoading}
          skeletonRow={<OrderListSkeletonRow />}
        >
          {orders.map((order) => (
            <OrderListRow
              isOpen={openOrderIds.includes(order.id)}
              key={order.id}
              onSelect={handleSelect}
              onToggle={handleToggle}
              order={order}
            />
          ))}
        </ListGrid>
      </Page>
      <Outlet />
    </>
  );
};
